import i18n from 'i18next';
import { round, isValidNum, toNum } from '@oliasoft-open-source/units';
import translations from 'src/internationalisation/translation-map.json';
import { costTrackerFields } from 'src/enums/cost-tracker';
import { CellWithExpandingIcon } from 'views/time-tracker/cell-with-expanding-icon';
import { formatTimeToUI } from 'views/time-tracker/utils/date/dateUtils';
import { CostTrackerItem, GroupedCostTrackerItem } from 'src/common/interfaces/cost-tracker.interfaces';
import { APIActionPayload } from 'api/api';
import { DebouncedFunc } from 'lodash';
import { ChangeEvent, MutableRefObject } from 'react';
import { BiPieChart } from 'react-icons/bi';
import { CostTypeMap } from 'src/enums/cost-setup';
import { Spacer, Text } from '@oliasoft-open-source/react-ui-library';
import { thousandsSeparators, removeSpaceSeparators } from './utils/utils';
import { getFields } from './utils/get-ct-fields';

type TCellValueUpdate = {
  sectionId: string | null;
  operationId: string;
  costTrackerItemId: string;
  value: string | number | null;
  field: string;
};

type TBildCostTrackerItem = {
  data: CostTrackerItem | GroupedCostTrackerItem;
  disabledFields: string[];
  updateTask?: MutableRefObject<
    DebouncedFunc<
      (
        costTrackerItemId: string,
        data: { field: string; value: string | number | null },
      ) => { payload: APIActionPayload; type: string }
    >
  >;
  cellValueUpdated?: (arg: TCellValueUpdate) => void;
  isExpanded?: boolean;
  isOperation?: boolean;
  costsArray?: any[];
};

export const buildCostTrackerItem = ({
  data,
  disabledFields,
  updateTask,
  cellValueUpdated = (_arg: TCellValueUpdate) => null,
  isExpanded = true,
  isOperation = true,
}: TBildCostTrackerItem) => {
  const rowStyle = {
    style: {
      backgroundColor: isOperation ? '' : 'var(--color-background-table-header)',
      marginLeft: 20,
    },
  };
  const isSection = 'operations' in data;

  return getFields(disabledFields).map((fieldName, idx) => {
    switch (fieldName) {
      case costTrackerFields.START:
      case costTrackerFields.FINISH:
        if (!isOperation) {
          return {
            ...rowStyle,
            value: (
              <CellWithExpandingIcon
                cellContent={formatTimeToUI(data[fieldName])}
                index={idx}
                isExpanded={isExpanded}
                leftSpace={isSection ? 0 : 20}
                textAlign="left"
              />
            ),
          };
        }
        return {
          ...rowStyle,
          value: formatTimeToUI(data[fieldName]),
          type: 'Input',
          disabled: true,
        };
      case costTrackerFields.COST_AFE:
        return {
          ...rowStyle,
          value: thousandsSeparators(round(data[fieldName], 0)),
          type: isOperation ? 'Input' : 'Text',
          disabled: isOperation,
        };
      case costTrackerFields.COST_SUGGESTED:
        return {
          ...rowStyle,
          value: thousandsSeparators(round(data[fieldName], 0)),
          type: isOperation ? 'Input' : 'Text',
          disabled: isOperation,
        };
      case costTrackerFields.COST_TYPE:
        return {
          ...rowStyle,
          value: CostTypeMap[data.type as keyof typeof CostTypeMap],
          type: isOperation ? 'Input' : 'Text',
          disabled: isOperation,
        };
      case costTrackerFields.VARIANCE: {
        const variance =
          data.costActual && data.costAfe && isValidNum(data.costActual) && isValidNum(data.costAfe)
            ? round(data.costActual - data.costAfe, 0)
            : null;
        const style = {
          color: variance && variance > 0 ? 'var(--color-text-error)' : 'var(--color-text-success)',
        };
        return {
          style: {
            backgroundColor: isOperation ? 'var(--color-background-disabled)' : 'var(--color-background-table-header)',
          },
          value: <span style={style}>{thousandsSeparators(variance)}</span>,
        };
      }
      case costTrackerFields.NAME: {
        if (!isOperation) {
          return {
            style: {
              backgroundColor: 'var(--color-background-table-header)',
            },
            value: (
              <CellWithExpandingIcon
                cellContent={data[fieldName]}
                index={idx}
                isExpanded={isExpanded}
                textAlign="left"
                leftSpace={isSection ? 0 : 20}
              />
            ),
          };
        }
        return {
          style: {
            backgroundColor: 'var(--color-background-disabled)',
          },
          value: (
            <>
              <Spacer width={45} />
              <Text>{data[fieldName]}</Text>
            </>
          ),
        };
      }
      case costTrackerFields.SEVERAL_OPERATIONS_COST: {
        return data?.severalOperationsCost === null || data?.severalOperationsCost === undefined
          ? {}
          : {
              icon: <BiPieChart />,
              tooltip: `${data.name}: ${thousandsSeparators(round(data?.severalOperationsCost, 0)) ?? ''}`,
              type: 'Icon',
              width: '15px',
            };
      }
      default: {
        if (!isOperation) {
          return {
            ...rowStyle,
            value: typeof data[fieldName] === 'number' ? (thousandsSeparators(data[fieldName]) ?? '') : data[fieldName],
          };
        }
        return {
          ...rowStyle,
          value: typeof data[fieldName] === 'number' ? (thousandsSeparators(data[fieldName]) ?? '') : data[fieldName],
          type: 'Input',
          allowEmpty: true,
          onBlur: () => {
            if (!data[fieldName] && data[fieldName] !== 0) {
              const value = data.costSuggested;
              cellValueUpdated({
                sectionId: data.sectionId,
                operationId: data.parentId!,
                costTrackerItemId: (data as CostTrackerItem).costTrackerItemId,
                value: isValidNum(value) ? toNum(value) : null,
                field: fieldName,
              });
              if (updateTask) {
                updateTask.current((data as CostTrackerItem).costTrackerItemId, {
                  value: value ?? null,
                  field: fieldName,
                });
              }
            }
          },
          onChange: (e: ChangeEvent<HTMLInputElement>): void => {
            const { value } = e.target;
            cellValueUpdated({
              sectionId: data.sectionId,
              operationId: data.parentId!,
              costTrackerItemId: (data as CostTrackerItem).costTrackerItemId,
              value: isValidNum(value) && value !== '' ? toNum(value) : null,
              field: fieldName,
            });
            if (updateTask) {
              updateTask.current((data as CostTrackerItem).costTrackerItemId, {
                value: value === '' ? null : removeSpaceSeparators(value),
                field: fieldName,
              });
            }
          },
        };
      }
    }
  });
};

export const buildHeaders = (disabledFields: string[], primaryCurrency: string) => {
  const cellsMap = [
    {
      columnName: costTrackerFields.NAME,
      value: `${i18n.t(translations.section)} / ${i18n.t(translations.costTracker_operationCostItem)}`,
      unit: '',
    },
    {
      columnName: costTrackerFields.START,
      value: i18n.t(translations.timeTracker_startTime),
      unit: '',
    },
    {
      columnName: costTrackerFields.COST_TYPE,
      value: i18n.t(translations.costTracker_costType),
      unit: '',
    },
    {
      columnName: costTrackerFields.COST_AFE,
      value: i18n.t(translations.costTracker_costAfe),
      unit: primaryCurrency,
    },
    {
      columnName: costTrackerFields.COST_SUGGESTED,
      value: 'Suggested Actual Cost',
      unit: primaryCurrency,
    },
    {
      columnName: costTrackerFields.COST_ACTUAL,
      value: i18n.t(translations.costTracker_costActual),
      unit: primaryCurrency,
    },
    {
      columnName: costTrackerFields.VARIANCE,
      value: i18n.t(translations.costTracker_variance),
      unit: primaryCurrency,
    },
    {
      columnName: costTrackerFields.FINISH,
      value: i18n.t(translations.timeTracker_finishTime),
      unit: '',
    },
    {},
  ];

  const shownFields = cellsMap.filter(
    (item) => !disabledFields.some((disabledField) => disabledField === item.columnName),
  );
  const labelsRow = shownFields.map(({ columnName, value }) => ({
    columnName,
    value,
  }));
  const unitRow = shownFields.map(({ unit }) => ({
    value: unit,
    type: 'Unit',
  }));

  return [{ cells: labelsRow }, { cells: unitRow }];
};
